import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button, DatePicker, Flex, Form, Image, Input, Modal, Space, Table, Tag, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined, HistoryOutlined, StopOutlined } from '@ant-design/icons';
import { useSetState } from 'ahooks';
import Permission, { useAccess } from '@/components/permission';
import VideoPlayer from '@/components/ui/video-player';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import AuditStatusCell from '@/pages/drama-review/components/audit-status-cell';
import BadgeCell from '@/components/badge-cell';
import HeatCoefficientModal from '@/pages/drama-review/components/heat-coefficient-modal';
import ReviewModal, { type DramaActionMode } from '@/pages/drama-review/components/review-modal';
import { SortDirectionSelect, SortFieldSelect } from '@/pages/drama-review/components/sort-filter';
import StatusFilterSelect from '@/pages/drama-review/components/status-filter';
import {
    dramaExport,
    dramaList,
    dramaOffline,
    dramaReview,
    updateDramaBadge,
    updateDramaHeatCoefficient,
    type DramaBadge,
    type DramaEpisode,
    type DramaHistoryItem,
    type DramaItem,
    type DramaListParams,
    type DramaListStatusFilter,
    type DramaListDramaStatusFilter,
} from '@/services/dramas';
import { handleExportResponse } from '@/utils/export-response';
import {
    buildDramaListSort,
    canApproveDrama,
    canEditBadge,
    canOfflineDrama,
    canRejectDrama,
    dramaReviewStatusIntlId,
    DRAMA_DRAMA_STATUS_FILTER_OPTIONS,
    DRAMA_SORT_DIRECTION_OPTIONS,
    DRAMA_SORT_OPTIONS,
    DRAMA_STATUS_FILTER_OPTIONS,
    DEFAULT_DRAMA_DRAMA_STATUS_FILTER,
    dramaListDramaStatusFilterIntlId,
    dramaListStatusFilterIntlId,
    parseDramaStatusQueryParam,
    formatDramaTimestamp,
    formatHeatCoefficient,
    getAuditReason,
    getAuditStatus,
    getDramaCover,
    findDramaEpisodeMatch,
    getDramaEpisodeDomId,
    getDramaEpisodes,
    getDramaTitle,
    getDramaVideo,
    getEpisodeNo,
    getEpisodeTitle,
    getEpisodeVideo,
    isVideoAsset,
    resolveAssetUrl,
    reviewStatusTagColor,
    statusIntlId,
    statusTagColor,
    type DramaListSort,
    type DramaListSortDirection,
    type DramaListSortField,
} from '@/pages/drama-review/utils';

import type { ProColumns } from '@ant-design/pro-components';
import type { ColumnsType } from 'antd/es/table';
import type { PaginationProps } from 'antd';
import type { Dayjs } from 'dayjs';

import { isAbortError, RequestGuard } from '@/utils/request-guard';
import { resolveApiResult } from '@/utils/apiResponse';

const DRAMA_PERMISSION = 'Dramas_Dramas';
const DRAMA_REVIEW_PERMISSION = 'Dramas_Dramas_Review';
const DRAMA_BADGE_PERMISSION = 'Dramas_Dramas_Badge';
const DRAMA_EXPORT_PERMISSION = 'Dramas_Dramas_Export';
const DRAMA_OFFLINE_PERMISSION = 'Dramas_Dramas_Offline';
const DRAMA_HEAT_COEFFICIENT_PERMISSION = 'Dramas_Dramas_Heat-Coefficient';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

type DramaSearchFilters = Pick<DramaListParams, 'id' | 'title_fuzzy' | 'user_id' | 'online_at'>;

const pickSearchFilters = (values: Record<string, unknown>): DramaSearchFilters => {
    const next: DramaSearchFilters = {};
    (['id', 'title_fuzzy', 'user_id'] as const).forEach((key) => {
        const text = String(values[key] ?? '').trim();
        if (text) next[key] = text;
    });

    const onlineAtRange = values.online_at_range as [Dayjs, Dayjs] | undefined;
    if (onlineAtRange?.length === 2) {
        next.online_at = [
            onlineAtRange[0].startOf('d').toISOString(),
            onlineAtRange[1].endOf('d').toISOString(),
        ];
    }

    return next;
};

const CreatorInfoCell: React.FC<{ record: DramaItem }> = ({ record }) => (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div>
            <FormattedMessage id="dramaReview.drama.creatorUid" />：
            {record.user_id ? (
                <Typography.Text copyable={{ text: record.user_id }}>{record.user_id}</Typography.Text>
            ) : (
                '-'
            )}
        </div>
        <Typography.Text type="secondary">
            <FormattedMessage id="dramaReview.drama.createdAt" />：{formatDramaTimestamp(record.created_at)}
        </Typography.Text>
    </Space>
);

const DramaInfoCell: React.FC<{ record: DramaItem }> = ({ record }) => (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Typography.Text strong>
            <FormattedMessage id="dramaReview.drama.title" />：{getDramaTitle(record)}
        </Typography.Text>
        <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
            <FormattedMessage id="dramaReview.drama.description" />：{record.description || '-'}
        </Typography.Paragraph>
    </Space>
);

const MediaPreview: React.FC<{ src?: string; width?: number }> = ({ src, width = 80 }) => {
    if (!src) return <>-</>;

    const url = resolveAssetUrl(src);
    if (isVideoAsset(src)) {
        return <VideoPlayer src={url} width={width} />;
    }

    return <Image src={url} width={width} />;
};

const PageDramaReview: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const access = useAccess();

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [updatingBadgeId, setUpdatingBadgeId] = useState<string | null>(null);
    const [dataSource, setDataSource] = useState<DramaItem[]>([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [highlightedEpisodeId, setHighlightedEpisodeId] = useState<string | null>(null);
    const [actionRecord, setActionRecord] = useState<DramaItem | null>(null);
    const [actionMode, setActionMode] = useState<DramaActionMode>('reject');
    const [historyRecord, setHistoryRecord] = useState<DramaItem | null>(null);
    const [heatCoefficientRecord, setHeatCoefficientRecord] = useState<DramaItem | null>(null);
    const [heatCoefficientSubmitting, setHeatCoefficientSubmitting] = useState(false);
    const [statusFilter, setStatusFilter] = useState<DramaListStatusFilter | undefined>();
    const [dramaStatusFilter, setDramaStatusFilter] = useState<DramaListDramaStatusFilter | undefined>(
        DEFAULT_DRAMA_DRAMA_STATUS_FILTER,
    );
    const [searchFilters, setSearchFilters] = useState<DramaSearchFilters>({});
    const [sortField, setSortField] = useState<DramaListSortField | undefined>();
    const [sortDirection, setSortDirection] = useState<DramaListSortDirection>('desc');
    const [searchForm] = Form.useForm();
    const [searchParams] = useSearchParams();
    const deeplinkAppliedRef = useRef(false);
    const episodeDeeplinkRef = useRef<{
        episodeId?: string;
        episodeNo?: string;
        pending: boolean;
    }>({ pending: false });
    const [exporting, setExporting] = useState(false);
    const listRequestGuard = useMemo(() => new RequestGuard(), []);

    const sortParam = useMemo(
        () => buildDramaListSort(sortField, sortDirection),
        [sortDirection, sortField],
    );

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            const { data } = await dramaExport({
                page: pagination.current,
                size: pagination.pageSize,
                ...(statusFilter ? { status: statusFilter } : {}),
                ...(dramaStatusFilter ? { drama_status: dramaStatusFilter } : {}),
                ...(sortParam ? { sort: sortParam } : {}),
                ...searchFilters,
            });
            handleExportResponse(data, formatMessage({ id: 'dramaReview.export.success' }));
        } catch (error: any) {
            message.error(error?.response?.data?.msg || error?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    }, [
        formatMessage,
        message,
        pagination.current,
        pagination.pageSize,
        searchFilters,
        sortParam,
        statusFilter,
        dramaStatusFilter,
    ]);

    const fetchList = useCallback(
        async (
            page: number,
            pageSize: number,
            status?: DramaListStatusFilter,
            dramaStatus?: DramaListDramaStatusFilter,
            sort?: DramaListSort,
        ) => {
            const { id, signal } = listRequestGuard.begin();
            setLoading(true);
            try {
                const { data } = await dramaList(
                    {
                        page,
                        size: pageSize,
                        ...(status ? { status } : {}),
                        ...(dramaStatus ? { drama_status: dramaStatus } : {}),
                        ...(sort ? { sort } : {}),
                        ...searchFilters,
                    },
                    { signal },
                );
                if (!listRequestGuard.isCurrent(id)) return;

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const meta = data.meta;
                const defaultPageSize = DEFAULT_PAGINATION.pageSize ?? 20;
                const nextPage = meta?.page ?? page;
                const nextPageSize = meta?.size ?? pageSize;

                setPagination({
                    current:
                        typeof nextPage === 'number'
                            ? nextPage || 1
                            : Number(nextPage) > 0
                              ? Number(nextPage)
                              : 1,
                    pageSize:
                        typeof nextPageSize === 'number'
                            ? nextPageSize || defaultPageSize
                            : Number(nextPageSize) > 0
                              ? Number(nextPageSize)
                              : defaultPageSize,
                    total: meta?.total ?? data.data?.length ?? 0,
                });
                setDataSource(data.data ?? []);
            } catch (error: any) {
                if (isAbortError(error)) return;
                message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch dramas');
            } finally {
                if (listRequestGuard.isCurrent(id)) {
                    setLoading(false);
                }
            }
        },
        [listRequestGuard, message, searchFilters, setPagination],
    );

    const handleSearch = useCallback(() => {
        const nextFilters = pickSearchFilters(searchForm.getFieldsValue());
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters(nextFilters);
    }, [searchForm, setPagination]);

    const handleResetSearch = useCallback(() => {
        searchForm.resetFields();
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters({});
    }, [searchForm, setPagination]);

    const handleStatusFilterChange = useCallback(
        (status?: DramaListStatusFilter) => {
            setStatusFilter(status);
            setPagination((prev) => ({ ...prev, current: 1 }));
        },
        [setPagination],
    );

    const handleDramaStatusFilterChange = useCallback(
        (dramaStatus?: DramaListDramaStatusFilter) => {
            setDramaStatusFilter(dramaStatus);
            setPagination((prev) => ({ ...prev, current: 1 }));
        },
        [setPagination],
    );

    const handleSortFieldChange = useCallback(
        (field?: DramaListSortField) => {
            setSortField(field);
            setPagination((prev) => ({ ...prev, current: 1 }));
        },
        [setPagination],
    );

    const handleSortDirectionChange = useCallback(
        (direction: DramaListSortDirection) => {
            setSortDirection(direction);
            setPagination((prev) => ({ ...prev, current: 1 }));
        },
        [setPagination],
    );

    useEffect(() => {
        if (deeplinkAppliedRef.current) return;
        const id = searchParams.get('id')?.trim();
        if (!id) return;
        deeplinkAppliedRef.current = true;
        const episodeId = searchParams.get('episode_id')?.trim();
        const episodeNo = searchParams.get('episode_no')?.trim();
        if (episodeId || episodeNo) {
            episodeDeeplinkRef.current = { episodeId, episodeNo, pending: true };
        }
        const dramaStatusFromQuery = parseDramaStatusQueryParam(searchParams.get('drama_status'));
        if (searchParams.has('drama_status')) {
            setDramaStatusFilter(dramaStatusFromQuery);
        }
        searchForm.setFieldsValue({ id });
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters({ id });
    }, [searchForm, searchParams, setPagination]);

    useEffect(() => {
        if (!episodeDeeplinkRef.current.pending || loading) return;
        const dramaId = searchParams.get('id')?.trim();
        if (!dramaId) return;

        const drama = dataSource.find((item) => item.id === dramaId);
        if (!drama) return;

        const { episodeId, episodeNo } = episodeDeeplinkRef.current;
        episodeDeeplinkRef.current.pending = false;
        setExpandedRowKeys([drama.id]);

        const matchedEpisode = findDramaEpisodeMatch(
            getDramaEpisodes(drama),
            episodeId,
            episodeNo,
        );
        if (matchedEpisode?.id) {
            setHighlightedEpisodeId(matchedEpisode.id);
        }
    }, [dataSource, loading, searchParams]);

    useEffect(() => {
        if (!highlightedEpisodeId) return;

        const scrollTimer = window.setTimeout(() => {
            document
                .getElementById(getDramaEpisodeDomId({ id: highlightedEpisodeId }))
                ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 100);
        const highlightTimer = window.setTimeout(() => setHighlightedEpisodeId(null), 3000);

        return () => {
            window.clearTimeout(scrollTimer);
            window.clearTimeout(highlightTimer);
        };
    }, [highlightedEpisodeId, expandedRowKeys]);

    useEffect(() => {
        fetchList(
            pagination.current as number,
            pagination.pageSize as number,
            statusFilter,
            dramaStatusFilter,
            sortParam,
        );
    }, [fetchList, sortParam, statusFilter, dramaStatusFilter]);

    const openAction = useCallback((record: DramaItem, mode: DramaActionMode) => {
        setActionRecord(record);
        setActionMode(mode);
    }, []);

    const closeAction = useCallback(() => {
        setActionRecord(null);
    }, []);

    const handleActionSubmit = useCallback(
        async ({ reason }: { reason: string }) => {
            if (!actionRecord) return;

            const trimmed = reason?.trim() || '';
            setSubmitting(true);
            try {
                const { data } =
                    actionMode === 'offline'
                        ? await dramaOffline(actionRecord.id, { reason: trimmed })
                        : await dramaReview(actionRecord.id, {
                              approved: actionMode === 'approve',
                              auditReason: trimmed,
                          });

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                message.success(formatMessage({ id: 'c.operationCompleted' }));
                closeAction();
                await fetchList(
                    pagination.current as number,
                    pagination.pageSize as number,
                    statusFilter,
                    dramaStatusFilter,
                    sortParam,
                );
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        (actionMode === 'offline'
                            ? 'Offline failed'
                            : actionMode === 'approve'
                              ? 'Approve failed'
                              : 'Reject failed'),
                );
            } finally {
                setSubmitting(false);
            }
        },
        [
            actionMode,
            actionRecord,
            closeAction,
            fetchList,
            formatMessage,
            message,
            pagination.current,
            pagination.pageSize,
            sortParam,
            statusFilter,
            dramaStatusFilter,
        ],
    );

    const renderDramaActions = useCallback(
        (record: DramaItem) => {
            const status = getAuditStatus(record);
            const historyCount = record.history?.length ?? 0;
            const canApprove = canApproveDrama(record);
            const canReject = canRejectDrama(status);
            const canOffline = canOfflineDrama(status);

            return (
                <Space size={4} wrap>
                    <Permission permission={DRAMA_REVIEW_PERMISSION}>
                        <Button
                            type="link"
                            size="small"
                            icon={<CheckOutlined />}
                            disabled={!canApprove}
                            onClick={() => openAction(record, 'approve')}
                        >
                            <FormattedMessage id="dramaReview.approve.action" />
                        </Button>
                    </Permission>
                    <Permission permission={DRAMA_REVIEW_PERMISSION}>
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<CloseOutlined />}
                            disabled={!canReject}
                            onClick={() => openAction(record, 'reject')}
                        >
                            <FormattedMessage id="dramaReview.reject.action" />
                        </Button>
                    </Permission>
                    <Permission permission={DRAMA_OFFLINE_PERMISSION}>
                        <Button
                            type="link"
                            size="small"
                            icon={<StopOutlined />}
                            disabled={!canOffline}
                            onClick={() => openAction(record, 'offline')}
                        >
                            <FormattedMessage id="dramaReview.offline.action" />
                        </Button>
                    </Permission>
                    <Button
                        type="link"
                        size="small"
                        icon={<HistoryOutlined />}
                        disabled={historyCount === 0}
                        onClick={() => setHistoryRecord(record)}
                    >
                        <FormattedMessage id="dramaReview.history.action" />
                        {historyCount > 0 ? ` (${historyCount})` : ''}
                    </Button>
                </Space>
            );
        },
        [openAction],
    );

    const episodeColumns: ColumnsType<DramaEpisode> = useMemo(
        () => [
            {
                title: formatMessage({ id: 'dramaReview.episode.no' }),
                width: 80,
                render: (_, record, index) => getEpisodeNo(record, index),
            },
            {
                title: formatMessage({ id: 'dramaReview.episode.title' }),
                render: (_, record, index) => getEpisodeTitle(record, index),
            },
            {
                title: formatMessage({ id: 'dramaReview.episode.duration' }),
                width: 100,
                render: (_, record) =>
                    record.duration_sec != null && record.duration_sec !== '' ? `${record.duration_sec}s` : '-',
            },
            {
                title: formatMessage({ id: 'dramaReview.episode.playCount' }),
                width: 100,
                align: 'right' as const,
                render: (_, record) => record.play_count ?? '-',
            },
            {
                title: formatMessage({ id: 'dramaReview.episode.video' }),
                render: (_, record) => <MediaPreview src={getEpisodeVideo(record)} />,
            },
        ],
        [formatMessage],
    );

    const historyColumns: ColumnsType<DramaHistoryItem> = useMemo(
        () => [
            {
                title: formatMessage({ id: 'dramaReview.history.time' }),
                width: 180,
                render: (_, record) => formatDramaTimestamp(record.created_at),
            },
            {
                title: formatMessage({ id: 'dramaReview.history.status' }),
                width: 180,
                render: (_, record) =>
                    record.status ? (
                        <Tag color={reviewStatusTagColor(record.status)}>
                            <FormattedMessage
                                id={dramaReviewStatusIntlId(record.status)}
                                defaultMessage={record.status}
                            />
                        </Tag>
                    ) : (
                        '-'
                    ),
            },
            {
                title: formatMessage({ id: 'dramaReview.history.reason' }),
                ellipsis: true,
                render: (_, record) => record.reason || '-',
            },
            {
                title: formatMessage({ id: 'dramaReview.history.reviewerId' }),
                width: 220,
                render: (_, record) => {
                    const accountName = record.reviewer_account?.account;
                    const accountId =
                        record.reviewer_account?.id || record.reviewer_account_id;
                    if (!accountName && !accountId) return '-';

                    return (
                        <Space direction="vertical" size={0}>
                            {accountName ? (
                                <Typography.Text strong>{accountName}</Typography.Text>
                            ) : null}
                            {accountId ? (
                                <Typography.Text
                                    type="secondary"
                                    copyable={{ text: accountId }}
                                    style={{ fontSize: 12 }}
                                >
                                    {accountId}
                                </Typography.Text>
                            ) : null}
                        </Space>
                    );
                },
            },
        ],
        [formatMessage],
    );

    const expandedRowRender = useCallback(
        (drama: DramaItem) => {
            const episodes = getDramaEpisodes(drama);

            return (
                <div style={{ padding: '8px 16px 16px' }}>
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                        <FormattedMessage id="dramaReview.episode.section" />
                    </Typography.Title>
                    <Table<DramaEpisode>
                        bordered
                        size="small"
                        rowKey={(record, index) => record.id || String(index)}
                        columns={episodeColumns}
                        dataSource={episodes}
                        pagination={false}
                        locale={{ emptyText: formatMessage({ id: 'dramaReview.episode.empty' }) }}
                        onRow={(record) => ({
                            id: getDramaEpisodeDomId(record),
                            style:
                                record.id === highlightedEpisodeId
                                    ? { backgroundColor: 'var(--ant-color-warning-bg, #fffbe6)' }
                                    : undefined,
                        })}
                    />
                </div>
            );
        },
        [episodeColumns, formatMessage, highlightedEpisodeId],
    );

    const canManageBadge = access(DRAMA_BADGE_PERMISSION);
    const canManageHeatCoefficient = access(DRAMA_HEAT_COEFFICIENT_PERMISSION);
    const canExport = access(DRAMA_EXPORT_PERMISSION);

    const handleHeatCoefficientSubmit = useCallback(
        async ({ heat_coefficient }: { heat_coefficient: number }) => {
            if (!heatCoefficientRecord) return;

            setHeatCoefficientSubmitting(true);
            try {
                const { data } = await updateDramaHeatCoefficient(heatCoefficientRecord.id, {
                    heat_coefficient,
                });
                const result = resolveApiResult(data);
                if (!result.success) {
                    message.error(result.msg || formatMessage({ id: 'dramaReview.heatCoefficient.updateFailed' }));
                    return;
                }

                message.success(formatMessage({ id: 'c.operationCompleted' }));
                setHeatCoefficientRecord(null);
                await fetchList(
                    pagination.current as number,
                    pagination.pageSize as number,
                    statusFilter,
                    dramaStatusFilter,
                    sortParam,
                );
            } catch (error: any) {
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'dramaReview.heatCoefficient.updateFailed' }),
                );
            } finally {
                setHeatCoefficientSubmitting(false);
            }
        },
        [
            fetchList,
            formatMessage,
            heatCoefficientRecord,
            message,
            pagination.current,
            pagination.pageSize,
            sortParam,
            statusFilter,
            dramaStatusFilter,
        ],
    );

    const handleBadgeChange = useCallback(
        async (record: DramaItem, badge: DramaBadge) => {
            if (!canManageBadge || !canEditBadge(record)) return;

            const previousBadge = record.badge;
            setUpdatingBadgeId(record.id);
            setDataSource((prev) =>
                prev.map((item) => (item.id === record.id ? { ...item, badge } : item)),
            );

            try {
                const { data } = await updateDramaBadge(record.id, { badge });
                if (data.code !== 200) {
                    setDataSource((prev) =>
                        prev.map((item) =>
                            item.id === record.id ? { ...item, badge: previousBadge } : item,
                        ),
                    );
                    message.error(data.msg);
                    return;
                }

                message.success(formatMessage({ id: 'c.operationCompleted' }));
            } catch (error: any) {
                setDataSource((prev) =>
                    prev.map((item) =>
                        item.id === record.id ? { ...item, badge: previousBadge } : item,
                    ),
                );
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'dramaReview.badge.updateFailed' }),
                );
            } finally {
                setUpdatingBadgeId(null);
            }
        },
        [canManageBadge, formatMessage, message],
    );

    const columns = useMemo<ProColumns<DramaItem>[]>(
        () => [
            {
                dataIndex: 'index',
                title: 'No.',
                valueType: 'index',
                width: 60,
            },
            {
                title: <FormattedMessage id="dramaReview.drama.info" />,
                width: 240,
                render: (_, record) => <DramaInfoCell record={record} />,
            },
            {
                title: <FormattedMessage id="dramaReview.drama.creatorInfo" />,
                width: 280,
                render: (_, record) => <CreatorInfoCell record={record} />,
            },
            {
                title: <FormattedMessage id="dramaReview.drama.cover" />,
                width: 120,
                render: (_, record) => <MediaPreview src={getDramaCover(record)} width={64} />,
            },
            {
                title: <FormattedMessage id="dramaReview.drama.preview" />,
                width: 120,
                render: (_, record) => <MediaPreview src={getDramaVideo(record)} width={64} />,
            },
            {
                title: <FormattedMessage id="dramaReview.dramaStatus" />,
                width: 110,
                render: (_, record) => {
                    const status = getAuditStatus(record);
                    return (
                        <Tag color={statusTagColor(status)}>
                            <FormattedMessage id={statusIntlId(status)} defaultMessage={status || '-'} />
                        </Tag>
                    );
                },
            },
            {
                title: <FormattedMessage id="dramaReview.adminReviewStatus.label" />,
                width: 200,
                render: (_, record) => <AuditStatusCell record={record} />,
            },
            {
                title: <FormattedMessage id="dramaReview.auditReason" />,
                width: 200,
                ellipsis: true,
                render: (_, record) => getAuditReason(record) || '-',
            },
            {
                title: <FormattedMessage id="dramaReview.drama.onlineAt" />,
                width: 180,
                render: (_, record) => formatDramaTimestamp(record.online_at),
            },
            {
                title: <FormattedMessage id="dramaReview.drama.offlineAt" />,
                width: 180,
                render: (_, record) => formatDramaTimestamp(record.offline_at),
            },
            {
                title: <FormattedMessage id="dramaReview.badge.label" />,
                width: 160,
                render: (_, record) => (
                    <BadgeCell
                        value={record.badge}
                        editable={Boolean(canManageBadge && canEditBadge(record))}
                        loading={updatingBadgeId === record.id}
                        onConfirm={(badge) => handleBadgeChange(record, badge)}
                    />
                ),
            },
            {
                title: <FormattedMessage id="dramaReview.heatCoefficient.label" />,
                width: 180,
                render: (_, record) => (
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Space size={4}>
                            <Typography.Text>{formatHeatCoefficient(record.heat_coefficient)}</Typography.Text>
                            {canManageHeatCoefficient ? (
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<EditOutlined />}
                                    style={{ padding: 0, height: 'auto' }}
                                    onClick={() => setHeatCoefficientRecord(record)}
                                >
                                    <FormattedMessage id="dramaReview.heatCoefficient.edit" />
                                </Button>
                            ) : null}
                        </Space>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            <FormattedMessage id="dramaReview.heatCoefficient.updatedAt" />：
                            {formatDramaTimestamp(record.heat_coefficient_updated_at)}
                        </Typography.Text>
                    </Space>
                ),
            },
            {
                title: <FormattedMessage id="dramaReview.table.actions" />,
                valueType: 'option',
                fixed: 'right',
                width: 280,
                render: (_, record) => renderDramaActions(record),
            },
        ],
        [canManageBadge, canManageHeatCoefficient, handleBadgeChange, renderDramaActions, updatingBadgeId],
    );

    const actionModalTitle = useMemo(() => {
        if (!actionRecord) return null;

        if (actionMode === 'offline') {
            return (
                <FormattedMessage
                    id="dramaReview.offline.confirm"
                    values={{ title: getDramaTitle(actionRecord) }}
                />
            );
        }
        if (actionMode === 'approve') {
            return (
                <FormattedMessage
                    id="dramaReview.confirmApproveDrama"
                    values={{ title: getDramaTitle(actionRecord) }}
                />
            );
        }
        return (
            <FormattedMessage
                id="dramaReview.confirmRejectDrama"
                values={{ title: getDramaTitle(actionRecord) }}
            />
        );
    }, [actionMode, actionRecord]);

    return (
        <Permission permission={DRAMA_PERMISSION}>
            <ProCard>
                <Form form={searchForm} onFinish={handleSearch} style={{ marginBottom: 16 }}>
                    <Flex gap={16} wrap>
                        <Form.Item
                            name="id"
                            style={{ marginBottom: 0 }}
                            getValueFromEvent={(e) => String(e?.target?.value ?? '').replace(/\D/g, '')}
                        >
                            <Input
                                allowClear
                                inputMode="numeric"
                                placeholder={formatMessage({ id: 'dramaReview.search.id' })}
                                style={{ width: 200 }}
                            />
                        </Form.Item>
                        <Form.Item name="title_fuzzy" style={{ marginBottom: 0 }}>
                            <Input
                                allowClear
                                placeholder={formatMessage({ id: 'dramaReview.search.titleFuzzy' })}
                                style={{ width: 200 }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="user_id"
                            style={{ marginBottom: 0 }}
                            getValueFromEvent={(e) => String(e?.target?.value ?? '').replace(/\D/g, '')}
                        >
                            <Input
                                allowClear
                                inputMode="numeric"
                                placeholder={formatMessage({ id: 'dramaReview.search.userId' })}
                                style={{ width: 200 }}
                            />
                        </Form.Item>
                        <Form.Item name="online_at_range" style={{ marginBottom: 0 }}>
                            <DatePicker.RangePicker
                                allowClear
                                placeholder={[
                                    formatMessage({ id: 'dramaReview.search.onlineAtStart' }),
                                    formatMessage({ id: 'dramaReview.search.onlineAtEnd' }),
                                ]}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            <FormattedMessage id="b.search" />
                        </Button>
                        <Button onClick={handleResetSearch}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                    </Flex>
                </Form>
                <AdminProTable<DramaItem>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey="id"
                    search={false}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            fetchList(page, pageSize, statusFilter, dramaStatusFilter, sortParam);
                        },
                    }}
                    toolBarRender={() => [
                        <StatusFilterSelect
                            key="drama-status"
                            value={dramaStatusFilter}
                            placeholderId="dramaReview.dramaStatus"
                            options={DRAMA_DRAMA_STATUS_FILTER_OPTIONS}
                            getLabelId={dramaListDramaStatusFilterIntlId}
                            onChange={handleDramaStatusFilterChange}
                        />,
                        <StatusFilterSelect
                            key="status"
                            value={statusFilter}
                            placeholderId="dramaReview.adminReviewStatus.label"
                            options={DRAMA_STATUS_FILTER_OPTIONS}
                            getLabelId={dramaListStatusFilterIntlId}
                            onChange={handleStatusFilterChange}
                        />,
                        <SortFieldSelect
                            key="sort-field"
                            value={sortField}
                            placeholderId="dramaReview.sort.label"
                            options={DRAMA_SORT_OPTIONS}
                            onChange={handleSortFieldChange}
                        />,
                        <SortDirectionSelect
                            key="sort-direction"
                            value={sortDirection}
                            disabled={!sortField}
                            options={DRAMA_SORT_DIRECTION_OPTIONS}
                            onChange={handleSortDirectionChange}
                        />,
                        canExport ? (
                            <Button key="export" loading={exporting} onClick={handleExport}>
                                <FormattedMessage id="dramaReview.export.button" />
                            </Button>
                        ) : null,
                    ]}
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                    }}
                    options={{
                        reload: () =>
                            fetchList(
                                pagination.current as number,
                                pagination.pageSize as number,
                                statusFilter,
                                dramaStatusFilter,
                                sortParam,
                            ),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 2280 }}
                />
            </ProCard>

            <ReviewModal
                open={Boolean(actionRecord)}
                mode={actionMode}
                title={actionModalTitle}
                loading={submitting}
                onCancel={closeAction}
                onSubmit={handleActionSubmit}
            />

            <HeatCoefficientModal
                open={Boolean(heatCoefficientRecord)}
                title={
                    heatCoefficientRecord ? (
                        <FormattedMessage
                            id="dramaReview.heatCoefficient.confirmTitle"
                            values={{ title: getDramaTitle(heatCoefficientRecord) }}
                        />
                    ) : null
                }
                initialValue={heatCoefficientRecord?.heat_coefficient}
                loading={heatCoefficientSubmitting}
                onCancel={() => setHeatCoefficientRecord(null)}
                onSubmit={handleHeatCoefficientSubmit}
            />

            <Modal
                open={Boolean(historyRecord)}
                title={
                    historyRecord ? (
                        <FormattedMessage
                            id="dramaReview.history.section"
                            defaultMessage="操作历史"
                        />
                    ) : null
                }
                footer={null}
                width={720}
                destroyOnClose
                onCancel={() => setHistoryRecord(null)}
            >
                {historyRecord ? (
                    <>
                        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                            {getDramaTitle(historyRecord)}
                            {historyRecord.history?.length
                                ? ` · ${formatMessage(
                                      { id: 'dramaReview.history.count' },
                                      { count: historyRecord.history.length },
                                  )}`
                                : ''}
                        </Typography.Paragraph>
                        <Table<DramaHistoryItem>
                            bordered
                            size="small"
                            rowKey={(record, index) => record.id || String(index)}
                            columns={historyColumns}
                            dataSource={historyRecord.history ?? []}
                            pagination={false}
                            locale={{ emptyText: formatMessage({ id: 'dramaReview.history.empty' }) }}
                        />
                    </>
                ) : null}
            </Modal>
        </Permission>
    );
};

export default PageDramaReview;
